import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { SxProps } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { FormTextField, FormConditionalTextField, FormCheckbox, FormDateTimeSelector } from '@ts-core/core/components/FormV2';

import ExercisesSelector, { useExercisesSelector } from 'modules/exercises/components/ExercisesSelector';
import { Exercise } from 'modules/exercises/types';

import Button from '@ts-core/components/Button';

import { AssignmentFormInput } from '../../types/form';
import { Assignment } from '../../types';
import { assignmentToAssignmentFormInput } from '../../utils/form';

interface AssignmentFormProps {
  sx?: SxProps;
  courseId: string;
  onSubmit: (data: AssignmentFormInput) => void;
  submitting?: boolean;

  defaultPositiveValue?: number;
  defaultNegativeValue?: number;
  minScoreToShowSolutions?: number;
  hideSolutionsUntilDueDate?: boolean;
  allowPracticeOnlyAfterExpired?: boolean;

  loading?: boolean;
  assignment?: Assignment;
  assignmentExercises?: Exercise[];
}

const AssignmentForm = (props: AssignmentFormProps) => {
  const {
    sx,
    onSubmit,
    loading,
    assignment,
    courseId,
    submitting,
    defaultNegativeValue = -1,
    defaultPositiveValue = 1,
    minScoreToShowSolutions = 0,
    hideSolutionsUntilDueDate = false,
    allowPracticeOnlyAfterExpired = false,
    assignmentExercises,
  } = props;

  const defaultValues = useMemo((): Partial<AssignmentFormInput> => {
    if (assignment) {
      return assignmentToAssignmentFormInput(assignment);
    }
    return {
      scores: {
        positive: defaultPositiveValue,
        negative: defaultNegativeValue,
      },
      minScoreToShowExplanation: minScoreToShowSolutions,
      showSolutionsOnlyAfterExpired: hideSolutionsUntilDueDate,
      allowPracticeOnlyAfterExpired: allowPracticeOnlyAfterExpired,
    };
  }, [assignment, defaultPositiveValue, defaultNegativeValue, minScoreToShowSolutions, hideSolutionsUntilDueDate, allowPracticeOnlyAfterExpired]);

  const { handleSubmit, control, watch } = useForm<AssignmentFormInput>({
    defaultValues,
  });
  const intl = useIntl();
  const timeLimit = watch('timeLimit');
  const { selectorProps, selectedExercises } = useExercisesSelector({ courseId, selectedExercises: assignmentExercises });

  const submitProxy = useCallback(
    (input: AssignmentFormInput) => {
      if (selectedExercises.length === 0) {
        return;
      }
      const exerciseIds = selectedExercises.map((exercise) => exercise.id);
      onSubmit({
        ...input,
        exercises: exerciseIds,
      });
    },
    [selectedExercises, onSubmit]
  );

  return (
    <Box width="100%" sx={sx}>
      <form noValidate onSubmit={handleSubmit(submitProxy)}>
        <Box width="100%">
          <Box width="100%" display="flex" justifyContent="flex-end">
            <Button color="secondary" type="submit" disabled={selectedExercises.length === 0} loading={submitting}>
              {intl.formatMessage({ id: 'button.save.title' })}
            </Button>
          </Box>
          <Stack spacing={2}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
              <FormTextField
                fullWidth
                label={intl.formatMessage({ id: 'field.name' })}
                control={control}
                name="name"
                rules={{ required: 'El nombre es requerido' }}
              />
              <FormDateTimeSelector
                sx={{ ml: { xs: 0, md: 2 }, mt: { xs: 2, md: 0 } }}
                fullWidth
                label={intl.formatMessage({ id: 'assignment.dueDate' })}
                control={control}
                name="expirationDate"
                rules={{ required: 'La fecha de entrega es requerida' }}
                DateTimePickerProps={{
                  disablePast: true,
                  ampm: false,
                }}
              />
            </Box>
            <Box display="flex" flexDirection="row" alignItems="flex-end">
              <FormTextField
                fullWidth
                label={intl.formatMessage({ id: 'field.assignment.positiveValue' })}
                control={control}
                name="scores.positive"
                rules={{ required: true }}
                type="number"
                inputProps={{ min: 0 }}
              />
              <FormTextField
                sx={{ ml: 2 }}
                fullWidth
                label={intl.formatMessage({ id: 'field.assignment.negativeValue' })}
                control={control}
                name="scores.negative"
                rules={{ required: true }}
                inputProps={{ max: 0 }}
                type="number"
              />
            </Box>
            <Stack direction="column" spacing={2} alignItems="flex-start" pt={2}>
              <FormConditionalTextField
                control={control}
                name="minScoreToShowExplanation"
                checkboxLabel={intl.formatMessage({ id: 'assignment.hideSolutionsBasedOnScore' })}
                tooltip={intl.formatMessage({ id: 'field.hideAssignmentSolutionsBasedOnScore' })}
                label={intl.formatMessage({ id: 'assignment.minScoreToShowSolution' })}
                type="number"
                inputProps={{ min: 0, max: 100 }}
                defaultValue={0}
                deactivatedValue={0}
              />
              <FormCheckbox
                control={control}
                name="showOnlyTotalScore"
                label={intl.formatMessage({ id: 'assignment.showOnlyTotalScore' })}
                tooltip={intl.formatMessage({ id: 'assignment.showOnlyTotalScore.tooltip' })}
              />
              <FormCheckbox
                control={control}
                name="showSolutionsOnlyAfterExpired"
                label={intl.formatMessage({ id: 'assignment.hideSolutionsUntilDueDate' })}
                tooltip={intl.formatMessage({ id: 'field.hideSolutionUntilDueDate.tooltip' })}
              />
              <FormCheckbox
                control={control}
                name="allowPracticeOnlyAfterExpired"
                label={intl.formatMessage({ id: 'field.disablePracticeUntilDueDate.label' })}
                tooltip={intl.formatMessage({ id: 'field.disablePracticeUntilDueDate.tooltip' })}
              />
              <FormConditionalTextField
                control={control}
                name="timeLimit"
                checkboxLabel={intl.formatMessage({ id: 'assignment.timeLimit' })}
                label={intl.formatMessage({ id: 'minutes' }, { value: 2 })}
                type="number"
                inputProps={{ min: 0, max: 100 }}
                defaultValue={0}
              />
              {timeLimit > 0 && (
                <FormCheckbox
                  label={intl.formatMessage({ id: 'field.allowSendAssignmentAnswerAfterTimeLimit.label' })}
                  control={control}
                  name="allowAnswersAfterTimeLimit"
                />
              )}
            </Stack>
            {!loading && (!assignment || !assignment.sent) && <ExercisesSelector {...selectorProps} />}
          </Stack>
        </Box>
      </form>
    </Box>
  );
};

export default AssignmentForm;
